'use strict';

import PubSub from 'pubsub-js';
import { Points, Vector3 } from 'three';

import { SkyviewAssetPositionProps } from '../../react/components/skyview/tooltip/skyview-asset-position-tooltip';
import { MetaDefinition } from '../../services/metadata/meta-types';
import { publishNotification } from '../../utils/notifications/publish-notification';
import { AssetManager } from '../asset-manager';
import cameraManager from '../camera-manager';
import { CoordinateManager } from '../coordinate-manager';
import { CellGridVolumeModel } from '../elements/volume/model/cell-grid-volume-model';
import { PrismVolumeModel } from '../elements/volume/model/prism-volume-model';
import { VolumeCalculationAssetTypeGuard } from '../elements/volume/types';
import { getAttributePositionAtIndex } from '../geodata/point-clouds/cesium-point-cloud/points/buffer-attribute/position-buffer-attribute';
import { InputHandler } from '../input-handler';
import { KeyInputData, MouseInputData } from '../utilities/input-event/input-event-data-extractor';
import { Pick } from '../utilities/pick';
import { PickingSphere } from './picking-sphere';
import { Tool } from './tool';
import { copyCoordinate } from './utils/context-menu/commands';

type VolumeInspectorContextMenuMetaData = {
  left: number;
  top: number;
  height: number;
  position: Vector3;
  enabled: boolean;
};

export class VolumeInspectorTool extends Tool {
  tooltipCallback?: (tooltip?: SkyviewAssetPositionProps) => void = undefined;
  contextMenuMetaData: VolumeInspectorContextMenuMetaData | undefined = {
    left: 0,
    top: 0,
    height: 0,
    position: new Vector3(),
    enabled: false,
  };

  private meta: MetaDefinition;

  private cellGridModel?: CellGridVolumeModel;
  private cursor: PickingSphere;

  constructor(inputHandler: InputHandler) {
    super('volume-inspector', inputHandler);

    this.visible = false;

    this.meta = { src: 'tool.volume-inspector', parameters: [] };

    this.cursor = new PickingSphere();
    this.add(this.cursor);
  }

  onTooltipChanged(callback: (tooltip?: SkyviewAssetPositionProps) => void) {
    this.tooltipCallback = callback;
  }

  clearTooltip() {
    this.tooltipCallback?.();
  }

  getMeta(sweref?: string): MetaDefinition {
    return this.meta;
  }

  onKeyDown(input: KeyInputData) {
    this.inputHandler.handleKeyPress(
      input,
      'c',
      () => {
        if (this.contextMenuMetaData) {
          void copyCoordinate(this.contextMenuMetaData.position).action();
        }
      },
      'Control',
    );
  }

  onMouseMove(e: MouseInputData) {
    if (!this.cellGridModel) {
      return true;
    }

    const intersection = Pick.instance.volumePoint(
      e.pointer.position.x,
      e.pointer.position.y,
      this.cellGridModel,
    );

    const camera = cameraManager().getActiveCamera();
    if ((intersection?.object as Points)?.geometry && intersection?.index && camera) {
      const volumePointPosition = intersection.object.localToWorld(
        getAttributePositionAtIndex(intersection.object as Points, intersection.index),
      );
      this.position.set(volumePointPosition.x, volumePointPosition.y, volumePointPosition.z);
      const pointHeight = (intersection.object as Points).geometry.attributes.height.array[
        intersection.index
      ];
      const pointPosition = CoordinateManager.getInstance().localToProject(
        intersection.point.x,
        intersection.point.y,
      );
      const screenPosition = camera.getScreenPosition(intersection.point);

      const baseHeight =
        CoordinateManager.getInstance().getMAS() +
        intersection.point.z -
        pointHeight -
        intersection.object.parent!.position.z;

      this.cursor.visible = true;
      this.contextMenuMetaData = {
        left: screenPosition.x,
        top: screenPosition.y,
        height: pointHeight,
        position: new Vector3(pointPosition.x, pointPosition.y, baseHeight),
        enabled: true,
      };
    } else {
      this.cursor.visible = false;
      this.contextMenuMetaData = undefined;
    }

    this.updateTooltip();

    return true;
  }

  updateTooltip() {
    if (!this.contextMenuMetaData) {
      this.clearTooltip();
      return;
    }

    this.tooltipCallback?.({
      properties: {
        type: 'volume-inspector',
        height: this.contextMenuMetaData.height,
        position: this.contextMenuMetaData.position,
      },
      screenPoint: {
        x: this.contextMenuMetaData.left,
        y: this.contextMenuMetaData.top,
      },
      coordinateSystem: '',
      coordinate: {
        easting: this.contextMenuMetaData.position.x,
        height: this.contextMenuMetaData.position.z,
        northing: this.contextMenuMetaData.position.y,
      },
    });
  }

  onMouseWheel(e: MouseInputData) {
    return true;
  }

  async enable() {
    this.visible = true;
    this.inputHandler.setCursor('crosshair');
    await this.loadTargetVolume();
  }

  async loadTargetVolume() {
    const selected = AssetManager.instance.getSelected();
    if (VolumeCalculationAssetTypeGuard.check(selected)) {
      const volumeHandler = selected.volumeHandler;
      const selectedVolume = volumeHandler.activeVolume;

      // Exit tool if the active volume is a prism volume.
      if (volumeHandler.volumeModel instanceof PrismVolumeModel) {
        this.inputHandler.setTool('select');
        return;
      }

      if (selectedVolume) {
        PubSub.publish('volumeInspectorToggleModal', {
          display: true,
          volumeHandler,
        });

        if (!volumeHandler.isModelVisible()) {
          await volumeHandler.toggleModelVisibility();
        }

        this.cellGridModel = volumeHandler.volumeModel;

        if (!this.cellGridModel?.containsHeightData) {
          publishNotification('err1111');
          this.inputHandler.setTool('select');
          return;
        }

        this.cellGridModel.displayGradient = true;

        return;
      }
    }

    this.inputHandler.setTool('select');
  }

  disable() {
    this.visible = false;
    this.inputHandler.setCursor('');
    this.cellGridModel = undefined;

    PubSub.publish('volumeInspectorToggleModal', {
      display: false,
    });
  }
}
